import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiError } from "../../lib/api";
import { PageHeader, Card, Button, Input, Select, Empty, Badge } from "../../components/admin/UI";
import { Modal, useModal } from "../../components/admin/Modal";
import { KeyRound, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { canManageSensitiveMemberActions } from "../../lib/memberPermissions";

const ROLES = [
    { v: "club_admin", l: "Admin Club" },
    { v: "coach", l: "Coach" },
    { v: "staff", l: "Staff" },
];

const ROLE_LABEL = Object.fromEntries(ROLES.map((r) => [r.v, r.l]));
const ROLE_COLOR = { club_admin: "orange", coach: "blue", staff: "emerald", parent: "amber", player: "slate", sponsor: "blue", visitor: "slate", super_admin: "red" };

export default function Members() {
    const { activeClubId, isSuperAdmin, logout, user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const { open, openModal, closeModal } = useModal();
    const empty = { email: "", full_name: "", phone: "", password: "", role: "coach" };
    const [form, setForm] = useState(empty);
    const [resetLink, setResetLink] = useState("");
    const [emailTarget, setEmailTarget] = useState(null);
    const [email, setEmail] = useState("");

    const createResetLink = async (id) => {
        try {
            const { data } = await api.post(`/users/${id}/password-reset`, null, { params: { club_id: activeClubId } });
            const path = `/reinitialisation-mot-de-passe/${encodeURIComponent(data.reset_token)}`;
            setResetLink(new URL(path, window.location.origin).href);
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const copyResetLink = async () => {
        await navigator.clipboard.writeText(resetLink);
        toast.success("Lien copié");
    };

    const load = () => {
        if (!activeClubId) return;
        api.get("/users", { params: { club_id: activeClubId } }).then((r) => setUsers(r.data));
    };
    useEffect(load, [activeClubId]);

    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/users", {
                email: form.email,
                full_name: form.full_name,
                phone: form.phone,
                password: form.password,
                roles: [{ club_id: activeClubId, role: form.role }],
            }, { params: { club_id: activeClubId } });
            toast.success("Membre créé");
            closeModal();
            setForm(empty);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const remove = async (id) => {
        if (!window.confirm("Retirer ce membre du club ?")) return;
        await api.delete(`/users/${id}`, { params: { club_id: activeClubId } });
        load();
    };

    const saveEmail = async (event) => {
        event.preventDefault();
        try {
            await api.patch(`/users/${emailTarget.id}/email`, { email });
            toast.success("Email mis à jour. Les sessions existantes ont été invalidées.");
            setEmailTarget(null);
            if (emailTarget.id === currentUser.id) {
                await logout();
            } else {
                load();
            }
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div data-testid="members-page">
            <PageHeader
                title="Membres & Permissions"
                subtitle="Gérez les utilisateurs ayant accès au club"
                action={<Button onClick={openModal} data-testid="add-member-button"><Plus className="w-4 h-4" /> Nouveau membre</Button>}
            />

            {users.length === 0 ? (
                <Empty title="Aucun membre" hint="Créez le premier compte." />
            ) : (
                <Card>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50">
                                <tr className="text-left">
                                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">Nom</th>
                                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">Email</th>
                                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">Rôles</th>
                                    <th className="px-4 py-3 text-right text-xs uppercase tracking-wider text-slate-500">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((u) => {
                                    const canManageSensitiveActions = canManageSensitiveMemberActions(isSuperAdmin, u);
                                    return <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50">
                                        <td className="px-4 py-3 font-semibold text-slate-900">{u.full_name}</td>
                                        <td className="px-4 py-3">{u.email}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-1">
                                                {u.roles?.filter((r) => r.club_id === activeClubId).map((r, i) => (
                                                    <Badge key={i} color={ROLE_COLOR[r.role] || "slate"}>{ROLE_LABEL[r.role] || r.role}</Badge>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            {isSuperAdmin && <button title="Modifier l'email" aria-label="Modifier l'email" onClick={() => { setEmailTarget(u); setEmail(u.email); }} className="mr-3 text-slate-500 hover:text-orange-600"><Pencil className="w-4 h-4 inline" /></button>}
                                            {canManageSensitiveActions && <>
                                                <button title="Créer un lien de réinitialisation" aria-label="Créer un lien de réinitialisation" onClick={() => createResetLink(u.id)} className="mr-3 text-slate-500 hover:text-orange-600"><KeyRound className="w-4 h-4 inline" /></button>
                                                <button onClick={() => remove(u.id)} className="text-slate-500 hover:text-red-500"><Trash2 className="w-4 h-4 inline" /></button>
                                            </>}
                                        </td>
                                    </tr>
                                })}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}

            <Modal open={open} onClose={closeModal} title="Nouveau membre">
                <form onSubmit={submit} className="space-y-4" data-testid="member-form">
                    <Input label="Nom complet" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required />
                    <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                    <Input label="Téléphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                    <Input label="Mot de passe" type="password" minLength={8} maxLength={128} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
                    <Select label="Rôle" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                        {ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
                    </Select>
                    <div className="flex justify-end gap-2">
                        <Button variant="secondary" type="button" onClick={closeModal}>Annuler</Button>
                        <Button type="submit" data-testid="member-submit">Créer</Button>
                    </div>
                </form>
            </Modal>
            <Modal open={Boolean(resetLink)} onClose={() => setResetLink("")} title="Lien de réinitialisation">
                <p className="text-sm text-slate-600 mb-3">Copiez ce lien maintenant et transmettez-le de manière sécurisée. Il ne sera plus affiché après fermeture.</p>
                <Input label="Lien à usage unique" value={resetLink} readOnly />
                <div className="mt-4 flex justify-end gap-2">
                    <Button variant="secondary" onClick={() => setResetLink("")}>Fermer</Button>
                    <Button onClick={copyResetLink}>Copier le lien</Button>
                </div>
            </Modal>
            <Modal open={Boolean(emailTarget)} onClose={() => setEmailTarget(null)} title="Modifier l'email">
                <form onSubmit={saveEmail} className="space-y-4">
                    <p className="text-sm text-slate-600">Le compte, son mot de passe, ses rôles et ses données liées seront conservés. Toutes ses sessions seront invalidées.</p>
                    <Input label="Nouvel email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
                    <div className="flex justify-end gap-2">
                        <Button variant="secondary" type="button" onClick={() => setEmailTarget(null)}>Annuler</Button>
                        <Button type="submit">Enregistrer</Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
}
